import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { MoreHorizontal, Edit, CheckCircle2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCommissionTiers } from '@/hooks/use-creators'
import { useSeedCommissionTiers } from '@/hooks/use-mutations'
import { TierFormDialog } from './tier-form-dialog'
import { UI } from '@/lib/constants'
import { Skeleton } from '@/components/ui/skeleton'
import type { CommissionTier } from '@/lib/api/types'

/**
 * Render a commission tiers management UI with listing, seeding, and editing flows.
 *
 * Displays a loading skeleton while tiers are being fetched. If no tiers exist, shows an empty
 * state with actions to generate default tiers or open the dialog to create one manually.
 * When tiers are present, renders a table of tiers with position, name, commission rate,
 * monthly seedings, status, and per-row actions to edit a tier. Opening the form dialog allows
 * creating a new tier (when no tier is selected) or editing the selected tier.
 *
 * @returns The component's rendered JSX: a loading skeleton, an empty-state panel with seed/create
 * actions, or a table of commission tiers with an edit/create dialog.
 */
export function TierListTable() {
  const { data: tiers, isLoading } = useCommissionTiers()
  const seedTiers = useSeedCommissionTiers()

  const [selectedTier, setSelectedTier] = useState<CommissionTier | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleEdit = (tier: CommissionTier) => {
    setSelectedTier(tier)
    setIsDialogOpen(true)
  }

  const handleCreate = () => {
    setSelectedTier(null)
    setIsDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: UI.SKELETON_TABLE_ROWS }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  if (!tiers || tiers.length === 0) {
    return (
      <div className="flex h-[400px] shrink-0 items-center justify-center rounded-md border border-dashed">
        <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <CheckCircle2 className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">Tiers não configurados</h3>
          <p className="mb-4 mt-2 text-sm text-muted-foreground">
            Configure as regras de comissionamento para poder cadastrar novos creators no portal
            parceiro.
          </p>
          <div className="flex gap-2">
            <ChonkyButton
              variant="secondary"
              onClick={() => seedTiers.mutate()}
              loading={seedTiers.isPending}
            >
              Gerar Regras Padrão
            </ChonkyButton>
            <ChonkyButton onClick={handleCreate}>Criar Manualmente</ChonkyButton>
          </div>
        </div>
        <TierFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} tier={selectedTier} />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <ChonkyButton onClick={handleCreate}>Criar Novo Tier</ChonkyButton>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Posição</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Min. Vendas</TableHead>
            <TableHead>Taxa (%)</TableHead>
            <TableHead>Envios Mensais</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiers.map((tier) => (
            <TableRow key={tier.id}>
              <TableCell className="font-medium text-center">
                <Badge variant="secondary">{tier.position}</Badge>
              </TableCell>
              <TableCell className="font-medium">{tier.name}</TableCell>
              <TableCell>
                {tier.minSalesCount === 0 ? '—' : `${tier.minSalesCount} vendas`}
              </TableCell>
              <TableCell>{(tier.commissionRate * 100).toFixed(0)}%</TableCell>
              <TableCell>{tier.monthlySeedings} unidades</TableCell>
              <TableCell>
                <Badge variant={tier.isActive ? 'default' : 'secondary'}>
                  {tier.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Abrir menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(tier)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Editar Regras
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TierFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} tier={selectedTier} />
    </div>
  )
}
